import { z } from 'zod'

import type { Attribution } from '../attribution'
import { api, ApiError, authRequest } from './client'

const timezone = z.enum(['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'])
export type Timezone = z.infer<typeof timezone>

const outlet = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  address: z.string(),
  phone: z.string(),
  timezone,
  receipt_footer: z.string(),
  // The owner's profile of the business (migration 0019). An older server sends none of them.
  province: z.string().default(''),
  city: z.string().default(''),
  district: z.string().default(''),
  operating_period: z.enum(['', 'under_1', '1_3', '3_5', 'over_5']).default(''),
  email: z.string().default(''),
})
const business = z.object({ id: z.string(), name: z.string(), outlet })
export type Business = z.infer<typeof business>

const device = z.object({
  id: z.string(),
  letter: z.string(),
  status: z.enum(['active', 'revoked']),
  model: z.string(),
  app_version: z.string(),
  activated_at: z.string(),
  revoked_at: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  pending_count: z.number().int().nullable(),
  pending_reported_at: z.string().nullable(),
  // A replaced phone that may still send what it holds (ADR-0003 clause 5).
  flush_open: z.boolean().default(false),
})
export type Device = z.infer<typeof device>

const replacementInfo = z.object({
  active: z
    .object({
      id: z.string(),
      letter: z.string(),
      model: z.string(),
      last_synced_at: z.string().nullable(),
      pending_count: z.number().int().nullable(),
      pending_reported_at: z.string().nullable(),
      last_receipt_no: z.string().nullable(),
      // The running number of that receipt: the printed code is random and says nothing about position.
      last_receipt_seq: z.number().int().nullable().default(null),
    })
    .nullable(),
  safe_to_replace: z.boolean(),
})
export type ReplacementInfo = z.infer<typeof replacementInfo>

// ---------- reports (P4-05/06). Money is integer rupiah; the web never sums it itself.
const money = z.number().int()
const orderType = z.enum(['dine_in', 'takeaway'])
const staffLine = z.object({
  staff_id: z.string().nullable(),
  name: z.string(),
  services: z.number().int(),
  net: money,
  commission: money,
})
export type StaffLine = z.infer<typeof staffLine>
const summary = z.object({
  from: z.string(),
  to: z.string(),
  count: z.number().int(),
  gross: money,
  discount: money,
  net: money,
  // F&B lite (ADR-0014). Defaults keep an older server's answer readable.
  tax: money.default(0),
  service: money.default(0),
  takeaway_fee: money.default(0),
  rounding: z.number().int().default(0),
  void_count: z.number().int(),
  void_amount: money,
  by_day: z.array(z.object({ day: z.string(), count: z.number().int(), net: money })),
  by_order_type: z.array(z.object({ order_type: orderType.nullable(), count: z.number().int(), net: money })).default([]),
  by_method: z.array(z.object({ method: z.string(), count: z.number().int().default(0), amount: money })),
  by_item: z.array(z.object({ name: z.string(), quantity: z.number().int(), net: money })),
  by_staff: z.array(staffLine),
  by_category: z.array(z.object({ category: z.string(), quantity: money, net: money })).default([]),
})
export type Summary = z.infer<typeof summary>

// Completed sales per weekday (0 = Monday) and hour of the phone's own clock, worked out on the server.
const peakHours = z.object({
  from: z.string(),
  to: z.string(),
  cells: z.array(z.object({ weekday: z.number().int(), hour: z.number().int(), count: z.number().int(), net: money })),
})
export type PeakHours = z.infer<typeof peakHours>
export type PeakCell = PeakHours['cells'][number]

const saleRow = z.object({
  id: z.string(),
  receipt_no: z.string(),
  // The owner's running number (the receipt shows a random code instead). Absent from an older server.
  receipt_seq: z.number().int().nullable().default(null),
  business_day: z.string(),
  device_time: z.string(),
  utc_offset_minutes: z.number().int(),
  status: z.enum(['completed', 'voided']),
  gross_amount: money,
  discount_amount: money,
  net_amount: money,
  device_letter: z.string(),
  cashier_name: z.string(),
  methods: z.string().nullable(),
  order_type: orderType.nullable().default(null),
  table_label: z.string().nullable().default(null),
  position_note: z.string().nullable().default(null),
  takeaway_fee_amount: money.default(0),
  service_amount: money.default(0),
  tax_amount: money.default(0),
  rounding_amount: z.number().int().default(0),
})
export type SaleRow = z.infer<typeof saleRow>
const saleDetail = saleRow.omit({ methods: true }).extend({
  server_received_at: z.string(),
  tax_rate_bps: z.number().int().default(0),
  service_rate_bps: z.number().int().default(0),
  prices_include_tax: z.boolean().default(false),
  items: z.array(
    z.object({
      name: z.string(),
      item_type: z.string(),
      quantity: z.number().int(),
      unit_price: money,
      gross: money,
      discount: money,
      net: money,
      staff_name: z.string(),
      commission: money,
      note: z.string().nullable().default(null),
    }),
  ),
  payments: z.array(z.object({ method: z.string(), amount: money, reference: z.string().nullable() })),
  reversal: z
    .object({
      reason: z.string(),
      reversed_at: z.string(),
      business_day: z.string(),
      amount: money,
      authorized_by_name: z.string(),
    })
    .nullable(),
})
export type SaleDetail = z.infer<typeof saleDetail>

const cashSession = z.object({
  id: z.string(),
  business_day: z.string(),
  opened_at: z.string(),
  closed_at: z.string().nullable(),
  opening_cash: money,
  device_letter: z.string(),
  cash_sales: money,
  cash_in: money,
  cash_out: money,
  voids: money,
  expected_now: money,
  expected_cash: money.nullable(),
  actual_cash: money.nullable(),
  difference_amount: money.nullable(),
  notes: z.string().nullable(),
  // Opened by the phone itself while the cash drawer is off: left out of the expected cash and the closing differences.
  auto: z.boolean().default(false),
})
export type CashSession = z.infer<typeof cashSession>

const syncStatus = z.object({
  id: z.string(),
  letter: z.string(),
  status: z.enum(['active', 'revoked']),
  model: z.string(),
  app_version: z.string(),
  last_synced_at: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  rejected: z.number().int(),
  waiting: z.number().int(),
})
export type SyncStatus = z.infer<typeof syncStatus>

// ---------- master data (P4-08)
const catalogItem = z.object({
  id: z.string(),
  name: z.string(),
  item_type: z.enum(['service', 'product']),
  category: z.string(),
  price: money,
  cost: money.nullable(),
  active: z.boolean(),
  // Where the item sits among its category's items on the cashier screen; 0 for all of them is alphabetical (set on the phone).
  // Optional on a save: left out, the server keeps it.
  sort_order: z.number().int().optional(),
  version: z.number().int(),
})
export type CatalogItem = z.infer<typeof catalogItem>
const staff = z.object({ id: z.string(), name: z.string(), active: z.boolean(), version: z.number().int() })
export type Staff = z.infer<typeof staff>
const menuCategory = z.object({ id: z.string(), name: z.string(), active: z.boolean(), version: z.number().int() })
export type MenuCategory = z.infer<typeof menuCategory>
const rule = z.object({
  id: z.string(),
  catalog_item_id: z.string(),
  staff_id: z.string().nullable(),
  rule_type: z.enum(['fixed', 'percent']),
  rate: money,
  active: z.boolean(),
  version: z.number().int(),
})
export type Rule = z.infer<typeof rule>
const saved = z.object({ id: z.string(), version: z.number().int(), snapshot_version: z.number().int().nullable() })
const conflict = z.object({
  id: z.string(),
  entity_type: z.string(),
  entity_id: z.string(),
  entity_name: z.string().nullable(),
  field: z.string(),
  device_value: z.unknown(),
  server_value: z.unknown(),
  created_at: z.string(),
  seen_by_owner_at: z.string().nullable(),
})
export type Conflict = z.infer<typeof conflict>
const auditRow = z.object({
  id: z.number().int(),
  actor_type: z.string(),
  actor_id: z.string().nullable(),
  action: z.string(),
  entity_type: z.string().nullable(),
  entity_id: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()),
  created_at: z.string(),
})
export type AuditRow = z.infer<typeof auditRow>

const activationCode = z.object({ code: z.string(), kind: z.enum(['normal', 'replace']), expires_at: z.string() })
export type ActivationCode = z.infer<typeof activationCode>

const me = z.object({ user_id: z.string(), name: z.string(), phone: z.string() })

// ---------- cashier settings (ADR-0013). Names are the server's.
export const BUSINESS_TYPES = ['food', 'services', 'retail', 'other'] as const
export type BusinessType = (typeof BUSINESS_TYPES)[number]
const settings = z.object({
  business_type: z.enum(BUSINESS_TYPES),
  staff_per_line_enabled: z.boolean(),
  tax_enabled: z.boolean(),
  tax_rate_bps: z.number().int(),
  service_enabled: z.boolean(),
  service_rate_bps: z.number().int(),
  prices_include_tax: z.boolean(),
  rounding_unit: z.union([z.literal(0), z.literal(100)]),
  order_type_enabled: z.boolean(),
  takeaway_fee_amount: z.number().int(),
  table_number_enabled: z.boolean(),
  // The cashier must pick a table (or "Lainnya") before the menu; the list is what the buttons show.
  table_required: z.boolean().default(false),
  table_list: z.array(z.string()).default([]),
  item_notes_enabled: z.boolean(),
  note_presets: z.array(z.string()),
  receipt_show_notes: z.boolean(),
  kitchen_ticket_enabled: z.boolean(),
  kitchen_ticket_copies: z.number().int(),
  open_bill_enabled: z.boolean(),
  // Who the lock screen lists (false: the PIN alone says who signs in) and what each role may do (CASHIER_USERS_PLAN).
  lock_show_users: z.boolean().default(true),
  // Printing (docs/plans/PRINT_SETTINGS_PLAN.md): the digital receipt button, and how many times the customer's receipt is printed.
  receipt_digital_enabled: z.boolean().default(true),
  receipt_copies: z.number().int().default(1),
  // The cash drawer (docs/plans/CASH_DRAWER_OPTION_PLAN.md): off means no opening cash, no closing count, no cash in and out.
  cash_drawer_enabled: z.boolean().default(true),
  default_opening_cash: z.number().int().default(0),
  business_day_start: z.string().default('04:00'),
  business_day_end: z.string().default('23:59'),
  // Sample data (docs/plans/DEMO_DATA_SYNC_PLAN.md): also count it once the outlet has a real sale. Set in the sample data banner.
  show_demo_data: z.boolean().default(false),
  role_permissions: z.record(z.string(), z.array(z.string())).default({}),
  // Which lines the customer receipt shows and how (docs/plans/RECEIPT_LAYOUT_PLAN.md); the defaults are the receipt as it always was.
  receipt_show_logo: z.boolean().default(true),
  receipt_show_address: z.boolean().default(true),
  receipt_show_phone: z.boolean().default(true),
  receipt_show_cashier: z.boolean().default(true),
  receipt_show_order_line: z.boolean().default(true),
  receipt_show_item_detail: z.boolean().default(true),
  receipt_show_staff: z.boolean().default(true),
  receipt_show_payment_method: z.boolean().default(true),
  receipt_show_cash_change: z.boolean().default(true),
  receipt_show_footer: z.boolean().default(true),
  receipt_show_item_count: z.boolean().default(false),
  receipt_number_position: z.enum(['top', 'bottom', 'hidden']).default('top'),
  receipt_line_top: z.string().default(''),
  receipt_line_bottom: z.string().default(''),
  receipt_blank_lines: z.number().int().default(0),
  receipt_divider_style: z.enum(['dash', 'equals', 'none']).default('dash'),
  receipt_footer_align: z.enum(['center', 'left']).default('center'),
})
export type CashierSettings = z.infer<typeof settings>

// ---------- e-menu (ADR-0030)
const emenuSettings = z.object({
  enabled: z.boolean(),
  slug: z.string(),
  public_url: z.string(),
  photos_available: z.boolean(),
  ordering_enabled: z.boolean().default(false),
  cashier_seen_at: z.string().nullable().default(null),
})
const emenuTable = z.object({ id: z.string(), label: z.string(), url: z.string() })
export type EmenuTable = z.infer<typeof emenuTable>
export type EmenuSettings = z.infer<typeof emenuSettings>
const emenuPhoto = z.object({ item_id: z.string(), url: z.string() })
export type CashierSettingsChange = Partial<Omit<CashierSettings, 'business_type'>>

// ---------- cashier users (docs/plans/CASHIER_USERS_PLAN.md): the people who unlock the cashier phones
export type UserRole = 'owner' | 'manajer' | 'pengguna' | 'cashier'
const deviceUser = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(['owner', 'manajer', 'pengguna', 'cashier']),
  active: z.boolean(),
  version: z.number().int(),
})
export type DeviceUser = z.infer<typeof deviceUser>
const savedRow = z.object({ id: z.string(), version: z.number().int() })

// ---------- receipt logo (ADR-0013): a 1-bit bitmap, see logo.ts
const savedLogo = z.object({ width: z.number().int(), height: z.number().int(), data: z.string() })
export type SavedLogo = z.infer<typeof savedLogo>

// ---------- subscription (P6-10)
const order = z.object({
  order_id: z.string(),
  plan_code: z.string(),
  months: z.number().int(),
  gross_amount: z.number().int(),
  status: z.enum(['pending', 'paid', 'failed', 'expired', 'refunded']),
  redirect_url: z.string().nullable(),
  created_at: z.string(),
  paid_at: z.string().nullable(),
})
export type Order = z.infer<typeof order>
const subscription = z.object({
  state: z.enum(['trial', 'active', 'grace', 'expired']),
  plan: z.string(),
  entitled_until: z.string(),
  grace_until: z.string(),
  days_left: z.number().int(),
  trial_ends_at: z.string(),
  paid_until: z.string().nullable(),
  checkout_available: z.boolean(),
  plans: z.array(
    z.object({ code: z.string(), name: z.string(), months: z.number().int(), days: z.number().int(), price: z.number().int() }),
  ),
  orders: z.array(order),
})
export type Subscription = z.infer<typeof subscription>

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

// The sample data ("Data Contoh") the phones made and synced: what there is, and whether the figures include it right now.
const demoStatus = z.object({
  active: z.boolean(),
  sales: z.number().int(),
  voided: z.number().int(),
  revenue: money,
  from: z.string().nullable(),
  to: z.string().nullable(),
  has_real_sales: z.boolean(),
  show_demo_data: z.boolean(),
  in_figures: z.boolean(),
  cleared_at: z.string().nullable(),
})
export type DemoStatus = z.infer<typeof demoStatus>
const demoCleared = z.object({ removed_sales: z.number().int() })

export const endpoints = {
  demoStatus: () => api('/v1/demo-data', demoStatus),
  clearDemo: () => api('/v1/demo-data', demoCleared, { method: 'DELETE' }),
  login: (phone: string, password: string) => authRequest('/v1/auth/login', { phone, password }),
  signup: (data: {
    invite_code?: string
    phone: string
    password: string
    name: string
    business_name: string
    outlet_name: string
    outlet_code: string
    timezone: Timezone
    business_type: BusinessType
    business_subtype?: string
    business_type_note?: string
    phone_proof?: string
    attribution?: Attribution
  }) => authRequest('/v1/auth/signup', data),
  // Phone verification by SMS code. The server answers 503 `otp_disabled` while it is switched off.
  // What the signup form must ask for: an invite code only while the server still wants one.
  signupConfig: () =>
    api('/v1/auth/signup-config', z.object({ invite_required: z.boolean(), otp_enabled: z.boolean() })),
  otpRequest: (phone: string, invite_code?: string, purpose: 'REGISTER' | 'RESET' | 'REOPEN' = 'REGISTER') =>
    api('/v1/auth/otp/request', z.object({ expires_in: z.number().int(), resend_after: z.number().int() }), json('POST', { phone, invite_code, purpose })),
  otpVerify: (phone: string, otp: string, purpose: 'REGISTER' | 'RESET' | 'REOPEN' = 'REGISTER') =>
    api('/v1/auth/otp/verify', z.object({ phone_proof: z.string() }), json('POST', { phone, otp, purpose })),
  // A forgotten password: the proof of `otpVerify(..., 'RESET')` and the new password. Every session ends; sign in again.
  resetPassword: (phone: string, phone_proof: string, new_password: string) =>
    api('/v1/auth/password/reset', z.undefined(), json('POST', { phone, phone_proof, new_password })),
  // A closed account opened again (ADR-0025): the proof of `otpVerify(..., 'REOPEN')`. No sign-in is needed (it is closed).
  reopenAccount: (phone: string, phone_proof: string) =>
    api('/v1/account/reopen', z.undefined(), json('POST', { phone, phone_proof })),
  changePassword: (current_password: string, new_password: string) =>
    api('/v1/auth/password', z.object({ access_token: z.string() }), json('POST', { current_password, new_password })),
  logout: () => api('/v1/auth/logout', z.undefined(), { method: 'POST' }),
  /** Closes the account now (ADR-0025): every sign-in and device stops; the data is erased by an operator after the retention period. */
  deleteAccount: (password: string) => api('/v1/account/deletion', z.undefined(), json('POST', { password, source: 'web' })),
  me: () => api('/v1/auth/me', me),
  business: () => api('/v1/business', business),
  updateOutlet: (changes: Partial<Pick<Business['outlet'], 'address' | 'phone' | 'receipt_footer' | 'timezone' | 'province' | 'city' | 'district' | 'operating_period' | 'email'>>) =>
    api('/v1/business/outlet', business, json('PATCH', changes)),
  devices: () => api('/v1/devices', z.array(device)),
  createActivationCode: (kind: 'normal' | 'replace', acknowledge_unsent = false, old_phone_lost = false) =>
    api('/v1/devices/activation-codes', activationCode, json('POST', { kind, acknowledge_unsent, old_phone_lost })),
  /** Ends a replaced phone's right to send what it still holds (it was lost after all). */
  closeFlush: (deviceId: string) => api(`/v1/devices/${encodeURIComponent(deviceId)}/close-flush`, z.undefined(), { method: 'POST' }),
  replacementInfo: () => api('/v1/devices/replacement-info', replacementInfo),

  summary: (from: string, to: string, at?: Moments) => api(`/v1/reports/summary?${range(from, to, at)}`, summary),
  peakHours: (from: string, to: string, at?: Moments) => api(`/v1/reports/peak-hours?${range(from, to, at)}`, peakHours),
  sales: (from: string, to: string, q = '', offset = 0, at?: Moments, limit = 50) =>
    api(`/v1/reports/sales?${range(from, to, at)}&q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`, z.array(saleRow)),
  sale: (id: string) => api(`/v1/reports/sales/${encodeURIComponent(id)}`, saleDetail),
  cashSessions: (from: string, to: string) => api(`/v1/reports/cash-sessions?${range(from, to)}`, z.array(cashSession)),
  syncStatus: () => api('/v1/reports/sync-status', z.array(syncStatus)),

  catalog: () => api('/v1/catalog/items', z.array(catalogItem)),
  saveItem: (id: string, body: Omit<CatalogItem, 'id' | 'version'> & { expected_version: number }) =>
    api(`/v1/catalog/items/${id}`, saved, json('PUT', body)),
  staff: () => api('/v1/staff', z.array(staff)),
  saveStaff: (id: string, body: { name: string; active: boolean; expected_version: number }) =>
    api(`/v1/staff/${id}`, saved, json('PUT', body)),
  menuCategories: () => api('/v1/menu-categories', z.array(menuCategory)),
  saveMenuCategory: (id: string, body: { name: string; active: boolean; expected_version: number }) =>
    api(`/v1/menu-categories/${id}`, saved, json('PUT', body)),
  rules: () => api('/v1/commission-rules', z.array(rule)),
  saveRule: (id: string, body: Omit<Rule, 'id' | 'version'> & { expected_version: number }) =>
    api(`/v1/commission-rules/${id}`, saved, json('PUT', body)),
  conflicts: () => api('/v1/master-conflicts', z.array(conflict)),
  conflictsSeen: () => api('/v1/master-conflicts/seen', z.object({ marked: z.number().int() }), { method: 'POST' }),
  audit: () => api('/v1/audit?limit=500', z.array(auditRow)),

  /** The outlet's receipt logo, or null when it has none. */
  logo: async (): Promise<SavedLogo | null> => {
    try {
      return await api('/v1/business/logo', savedLogo)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_logo') return null
      throw e
    }
  },
  saveLogo: (body: SavedLogo) => api('/v1/business/logo', savedLogo, json('PUT', body)),
  deleteLogo: () => api('/v1/business/logo', z.undefined(), { method: 'DELETE' }),

  cashierSettings: () => api('/v1/business/settings', settings),
  deviceUsers: () => api('/v1/device-users', z.array(deviceUser)),
  createDeviceUser: (data: { name: string; role: Exclude<UserRole, 'owner'>; pin: string }) =>
    api('/v1/device-users', savedRow, json('POST', data)),
  updateDeviceUser: (
    id: string,
    data: { expected_version: number; name?: string; role?: Exclude<UserRole, 'owner'>; active?: boolean; pin?: string },
  ) => api(`/v1/device-users/${id}`, savedRow, json('PATCH', data)),
  saveCashierSettings: (changes: CashierSettingsChange) => api('/v1/business/settings', settings, json('PATCH', changes)),
  /** Changes the business type and resets the cashier settings to its defaults; nothing is deleted. The password is asked again. */
  applyProfile: (business_type: BusinessType, password: string) =>
    api('/v1/business/settings/apply-profile', settings, json('POST', { business_type, password })),

  emenuSettings: () => api('/v1/emenu/settings', emenuSettings),
  saveEmenuSettings: (changes: { enabled?: boolean; slug?: string; ordering_enabled?: boolean }) => api('/v1/emenu/settings', emenuSettings, json('PATCH', changes)),
  emenuTables: () => api('/v1/emenu/tables', z.array(emenuTable)),
  rotateEmenuTable: (id: string) => api(`/v1/emenu/tables/${encodeURIComponent(id)}/rotate`, emenuTable, { method: 'POST' }),
  emenuPhotos: () => api('/v1/emenu/photos', z.array(emenuPhoto)),
  /** The picture is the body, already made small by the browser (emenuPhoto.ts). */
  saveEmenuPhoto: (itemId: string, picture: Blob) =>
    api(`/v1/emenu/photos/${encodeURIComponent(itemId)}`, emenuPhoto, { method: 'PUT', body: picture, headers: { 'Content-Type': picture.type } }),
  deleteEmenuPhoto: (itemId: string) => api(`/v1/emenu/photos/${encodeURIComponent(itemId)}`, z.undefined(), { method: 'DELETE' }),

  subscription: () => api('/v1/billing/subscription', subscription),
  createOrder: (plan_code: string) => api('/v1/billing/orders', order, json('POST', { plan_code })),
  order: (id: string) => api(`/v1/billing/orders/${encodeURIComponent(id)}`, order),
  redeemVoucher: (code: string) =>
    api('/v1/billing/vouchers/redeem', z.object({ days: z.number().int(), paid_until: z.string().nullable() }), json('POST', { code })),
}

/** Two wall-clock moments ("2026-10-10T08:00", no zone) that narrow a report inside its days. */
export type Moments = { fromAt: string; toAt: string }

export function range(from: string, to: string, at?: Moments): string {
  const days = `from=${from}&to=${to}`
  return at ? `${days}&from_at=${encodeURIComponent(at.fromAt)}&to_at=${encodeURIComponent(at.toAt)}` : days
}
